import {
  EaseOptions,
  THREETween,
  useGsapBlur,
  useContext,
  useGsapOrbitAngles,
  useGsapOrbitPosition,
  useTimeline,
} from '@/features/animations';
import { usePerfManager } from '@/features/performance';
import { useExperienceStore } from '@/stores/useExperienceStore';
import { button, folder, useControls } from 'leva';
import { useEffect } from 'react';
import { useShallow } from 'zustand/shallow';

const folderName = "Animation";
export function useAnimations() 
{
    const shakeTl = useShake()
    const settleTl = useSettle()

    return { shake: shakeTl, settle: settleTl }
}

export function useShake() {
    const { actions, orbitControls, post, canvas, gl } = useExperienceStore(useShallow((state) => ({ actions: state.actions, orbitControls: state.orbitControls, pre: state.pre, post: state.post, canvas: state.canvas, gl: state.three.gl }))); //prettier-ignore
    const { setIsMonitorActive } = usePerfManager()
    const shakeCtx = useContext("shake")
    const shakeTl = useTimeline("shake", shakeCtx)
    const clipDuration = actions?.["Bottle"]?.getClip().duration ?? 0;

    // --- Angles ---   
    const adjustAngles = useGsapOrbitAngles()
    const [anglesConfig] = useControls(() => ({
        [folderName]: folder(
            {
                shake: folder(
                    {
                        angles: folder(
                            {
                                duration: { value: 1.0, min: 0, max: 10, step: 0.1 },
                                ease: {
                                value: EaseOptions.Power1In,
                                options: Object.values(EaseOptions),
                                },
                                minPolar: {
                                value: Math.PI * 0.5,
                                min: 0,
                                max: Math.PI,
                                step: 0.01,
                                },
                                maxPolar: {
                                value: Math.PI * 0.5,
                                min: 0,
                                max: Math.PI,
                                step: 0.01,
                                },
                                minAzimuth: {
                                value: 0.01,
                                min: -Math.PI,
                                max: Math.PI,
                                step: 0.01,
                                },
                                maxAzimuth: {
                                value: 0.01,
                                min: -Math.PI,
                                max: Math.PI,
                                step: 0.01,
                                },
                            },
                            { collapsed: true }
                        ),
                    },
                    { collapsed: true }
                ),
            },
            { collapsed: true }
        ),
    }));

    // --- Blur ---
    const applyBlur = useGsapBlur()
    const [blurConfig] = useControls(() => ({
        [folderName]: folder(
            {
                shake: folder(
                    {
                        blur: folder(
                            {
                                amount: { value: 15, min: 0, max: 50, step: 1 },
                                duration: {
                                value: clipDuration,
                                min: 0,
                                max: 10,
                                step: 0.1,
                                },
                                ease: {
                                value: EaseOptions.Power1In,
                                options: Object.values(EaseOptions),
                                },
                                yoyo: true,
                                repeat: 1,
                            },
                            { collapsed: true }
                        ),
                    },
                    { collapsed: true }
                ),
            },
            { collapsed: true }
        ),
    }), [clipDuration]);

    // --- Positions ---
    const adjustPosition = useGsapOrbitPosition()
    const [posConfig] = useControls(() => ({
        [folderName]: folder(
            {
                shake: folder(
                    {
                        position: folder(
                            {
                                duration: { value: clipDuration, min: 0, max: 20, step: 0.1 },
                                ease: {
                                value: EaseOptions.Power3InOut,
                                options: Object.values(EaseOptions),
                                },
                                position: 0,
                                keyframes: { value: [9, 3.5] },
                                play: button(() => {
                                  if (!orbitControls) return;
                                  adjustPosition(orbitControls, { ...posConfig, duration: clipDuration }, "position")
                                    .invalidate()
                                    .play();
                                })
                            },
                            { collapsed: true }
                        ),
                    },
                    { collapsed: true }
                ),
            },
            { collapsed: true }
        ),
    }), [clipDuration, orbitControls]); 

    // Timeline orchestration — creates tweens + wires them in one pass
    useEffect(() => {
        if (!orbitControls || !canvas?.domElement || !actions?.["Bottle"]) return;

        shakeTl.kill();
        shakeTl.clear();

        let pixelRatio = 0;
        const angles = adjustAngles(orbitControls, anglesConfig, "angles");
        const pos = adjustPosition(orbitControls, { ...posConfig, duration: clipDuration }, "position");
        const blur = applyBlur(canvas.domElement, { ...blurConfig, duration: clipDuration / 2 }, "blur"); // only half as it does a double animation (yoyo)
        const three = new THREETween(actions["Bottle"], "bottle");

        shakeTl
            .add(angles, 0)
            .addLabel("afterAngles", ">")
            .add(three, ">")
            .add(pos, "<")
            .add(blur, "<+=0.5") // 0.5s after pos starts

            // Performance optimizations to prevent blur from computing too much
            .eventCallback("onStart", () =>
            {
                pixelRatio = gl?.getPixelRatio() ?? 1; // save original
                setIsMonitorActive(false)
                gl?.setPixelRatio(1)
            })
            .eventCallback("onComplete", () =>
            {
                gl?.setPixelRatio(pixelRatio ?? 1)
                setIsMonitorActive(true)
                Object.assign(orbitControls, post.controls)
            })

        return () => shakeCtx.cleanup();
    }, [shakeTl, shakeCtx, canvas, post, orbitControls, actions, applyBlur, setIsMonitorActive, gl, adjustAngles, adjustPosition, clipDuration, JSON.stringify(blurConfig), JSON.stringify(anglesConfig), JSON.stringify(posConfig)]); //prettier-ignore

    // Leva
    useControls(folderName, {
        shake: folder({
        controls: folder({
            "play": button(async () => shakeTl.invalidate().play()),
            "reset": button(async () => shakeTl.reset()),
            pause: button(() => shakeTl?.pause()),
            playPause: button(() => shakeTl?.playPause(shakeTl.duration() * 0.5)),
            resume: button(() => shakeTl?.resume()),
            }, { collapsed: false, order: -1})
        })
    }, {collapsed: true},
    [shakeTl, shakeCtx, canvas, orbitControls, actions, applyBlur, adjustAngles, adjustPosition, clipDuration, JSON.stringify(blurConfig), JSON.stringify(anglesConfig), JSON.stringify(posConfig)])

    return shakeTl
}

export function useSettle() {
    const { orbitControls, pre, model } = useExperienceStore(useShallow((state) => ({ model:state.sceneGroup, orbitControls: state.orbitControls, pre: state.pre, post: state.post, canvas: state.canvas }))); //prettier-ignore
    const settleCtx = useContext("settle")
    const settleTl = useTimeline("settle", settleCtx)

    // --- Angles ---
    const adjustAngles = useGsapOrbitAngles()
    const [angConfig] = useControls(() => ({
        [folderName]: folder(
            {
                settle: folder(
                    {
                        angles: folder(
                            {
                                duration: { value: 1, min: 0, max: 10, step: 0.1 },
                                ease: {
                                value: EaseOptions.Power1InOut,
                                options: Object.values(EaseOptions),
                                },
                                minPolar: {
                                value: Math.PI * 0.5,
                                min: 0,
                                max: Math.PI,
                                step: 0.01,
                                },
                                maxPolar: {
                                value: Math.PI * 0.5,
                                min: 0,
                                max: Math.PI,
                                step: 0.01,
                                },
                                minAzimuth: {
                                value: 0.01,
                                min: -Math.PI,
                                max: Math.PI,
                                step: 0.01,
                                },
                                maxAzimuth: {
                                value: 0.01,
                                min: -Math.PI,
                                max: Math.PI,
                                step: 0.01,
                                },
                            },
                            { collapsed: true }
                        ),
                    },
                    { collapsed: true }
                ),
            },
            { collapsed: false }
        ),
    }));

    // --- Position ---
    const adjustPosition = useGsapOrbitPosition()
    const [posConfig, setPosConfig] = useControls(() => ({
        [folderName]: folder(
            {
                settle: folder(
                {
                    position: folder(
                    {
                        position: folder(
                            {
                                duration: { value: 1, min: 0, max: 10, step: 0.1 },
                                ease: {
                                    value: EaseOptions.Power3InOut,
                                    options: Object.values(EaseOptions),
                                },
                                position: { value: pre.controls.minDistance, min: 0, max: 20, step: 0.1 },
                            },
                            { collapsed: true }
                        ),
                    },
                    { order: 100, collapsed: true }
                    ),
                },
                { collapsed: true }
                ),
            },
            { collapsed: true }
        ),
    }), [pre.controls]);

    useEffect(() => {
        setPosConfig({ position: pre.controls.minDistance });
    }, [pre.controls.minDistance, setPosConfig]);

    // Timeline orchestration — creates tweens + wires them in one pass
    useEffect(() => {
        if (!orbitControls || !model) return;

        settleCtx.kill();
        settleTl.clear();

        const angles = adjustAngles(orbitControls, angConfig, "angles"); // happens here
        const pos = adjustPosition(orbitControls, posConfig, "position");
    
        settleTl
            .add(angles, "<") 
            .add(pos, "<")
            .eventCallback("onComplete", () => {Object.assign(orbitControls, pre.controls)});

        return () => settleCtx.cleanup();

    }, [settleTl, settleCtx, orbitControls, adjustAngles, adjustPosition, pre, JSON.stringify(angConfig), JSON.stringify(posConfig)],);

    // controls
    useControls(folderName, {
        settle: folder({
            controls: folder({
            "play": button(async () => settleTl.invalidate().play()),
            "reset": button(async () => settleTl.invalidate().reset()),
            pause: button(() => settleTl?.pause()),
            playPause: button(() => settleTl?.playPause(settleTl.duration() * 0.5)),
            resume: button(() => settleTl?.resume()),
            }, { collapsed: false, order: -10 })
        })
    }, {collapsed: false},
    [settleTl])

    return settleTl
}

// Legacy (don't delete)
  // Animation controls
  // const [animConfig, setAnimConfig] = useState({
  //   duration: 0.3,
  //   ease: "back.out(1.7)",
  //   scale: 0.9,
  //   exitDuration: 0.2,
  // });

  // Leva controls for animation
  // useControls(
  //   "Animation",
  //   {
  //     shake: folder({

  //     popup: folder(
  //       {
  //         duration: {
  //           value: animConfig.duration,
  //           min: 0.1,
  //           max: 2,
  //           step: 0.1,
  //           onChange: (v) => setAnimConfig((c) => ({ ...c, duration: v })),
  //         },
  //         ease: {
  //           value: animConfig.ease,
  //           options: [
  //             "back.out(1.7)",
  //             "power2.out",
  //             "elastic.out(1, 0.3)",
  //             "bounce.out",
  //             "circ.out",
  //           ],
  //           onChange: (v) => setAnimConfig((c) => ({ ...c, ease: v })),
  //         },
  //         scale: {
  //           value: animConfig.scale,
  //           min: 0.5,
  //           max: 1.5,
  //           step: 0.1,
  //           onChange: (v) => setAnimConfig((c) => ({ ...c, scale: v })),
  //         },
  //         exitDuration: {
  //           value: animConfig.exitDuration,
  //           min: 0.1,
  //           max: 1,
  //           step: 0.1,
  //           onChange: (v) => setAnimConfig((c) => ({ ...c, exitDuration: v })),
  //         },
  //         "Test Open": button(() => setIsShaking(true)),
  //         "Test Close": button(() => setIsShaking(false)),
  //       },
    
  //       { collapsed: true }

  //     ),
  //   },
  // )}, 
  //   [animConfig]
  // );

  // Animate popup in/out based on isShaking state
  // useGSAP(() => {
  //   if (!popUpRef.current) return;

  //   if (isShaking) {
  //     gsap.fromTo(
  //       popUpRef.current,
  //       { opacity: 0, scale: animConfig.scale },
  //       {
  //         opacity: 1,
  //         scale: 1,
  //         duration: props.ani?.duration() || animConfig.duration,
  //         ease: animConfig.ease,
  //       }
  //     );
  //   } else {
  //     gsap.to(popUpRef.current, {
  //       opacity: 0,
  //       scale: animConfig.scale,
  //       duration: animConfig.exitDuration,
  //     });
  //   }
  // }, [isShaking, props.ani, animConfig]);
